<template>
  <section class="faq-page">
    <header class="faq-header">
      <h1 class="faq-title">Frequently Asked Questions</h1>
      <p class="faq-subtitle">
        Have questions about our products and services? We have you covered.
      </p>
      <div class="faq-search-wrapper">
        <input v-model="searchTerm" type="text" class="faq-search-input" placeholder="Search..." />
        <span class="search-icon" aria-hidden="true">
          <!-- Inline magnifying glass SVG -->
          <svg
            width="25"
            height="24"
            viewBox="0 0 25 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M22.0306 20.4694L17.3365 15.7763C18.6971 14.1429 19.3755 12.0478 19.2307 9.92694C19.0859 7.80607 18.129 5.82268 16.5591 4.38935C14.9892 2.95602 12.9271 2.18311 10.8019 2.23141C8.67663 2.27971 6.65181 3.1455 5.14864 4.64867C3.64547 6.15184 2.77968 8.17666 2.73138 10.3019C2.68308 12.4272 3.45599 14.4892 4.88932 16.0591C6.32265 17.629 8.30604 18.5859 10.4269 18.7307C12.5478 18.8755 14.6428 18.1971 16.2762 16.8366L20.9693 21.5306C21.039 21.6003 21.1218 21.6556 21.2128 21.6933C21.3038 21.731 21.4014 21.7504 21.5 21.7504C21.5985 21.7504 21.6961 21.731 21.7871 21.6933C21.8782 21.6556 21.9609 21.6003 22.0306 21.5306C22.1003 21.4609 22.1556 21.3782 22.1933 21.2872C22.231 21.1961 22.2504 21.0985 22.2504 21C22.2504 20.9015 22.231 20.8039 22.1933 20.7128C22.1556 20.6218 22.1003 20.5391 22.0306 20.4694ZM4.24997 10.5C4.24997 9.16498 4.64585 7.85993 5.38755 6.7499C6.12925 5.63987 7.18346 4.77471 8.41686 4.26381C9.65026 3.75292 11.0075 3.61925 12.3168 3.8797C13.6262 4.14015 14.8289 4.78303 15.7729 5.72703C16.7169 6.67103 17.3598 7.87377 17.6203 9.18314C17.8807 10.4925 17.7471 11.8497 17.2362 13.0831C16.7253 14.3165 15.8601 15.3707 14.7501 16.1124C13.64 16.8541 12.335 17.25 11 17.25C9.21037 17.248 7.49463 16.5362 6.22919 15.2708C4.96375 14.0053 4.25196 12.2896 4.24997 10.5Z"
              fill="#767674"
            />
          </svg>
        </span>
      </div>
    </header>

    <div class="faq-list">
      <div v-for="(item, idx) in filteredFaqs" :key="idx" class="faq-item">
        <button class="faq-question" aria-label="FAQ Question" @click="toggle(idx)">
          <span class="toggle-icon" :class="{ open: openIndex === idx }" aria-hidden="true" />
          <span>{{ item.question }}</span>
        </button>
        <transition name="faq-collapse">
          <div v-show="openIndex === idx" class="faq-answer">
            <p
              v-for="(para, pIdx) in splitAnswer(item.answer)"
              :key="pIdx"
              aria-label="FAQ Answer"
              v-html="para"
            />
          </div>
        </transition>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  // name: 'render-faq-faq-page',
  props: {
    faqs: {
      type: [Array, String],
      default: () => [],
    },
  },
  data() {
    return {
      searchTerm: '',
      openIndex: 0, // first item open by default
      defaultFaqs: [
        {
          question: 'WHAT MATERIALS DO YOU USE TO MAKE YOUR BAGS AND TOTES?',
          answer:
            "We carefully select premium, sustainable materials for all our bags. Our leather goods are crafted using vegetable-tanned leather, which relies on natural tannins from tree bark instead of harmful chemicals. This traditional process creates beautiful, durable leather that actually improves with age.\n\nFor our canvas and fabric bags, we use certified organic cotton that's grown without pesticides or synthetic fertilizers. The cotton is sourced from farms that practice regenerative agriculture, helping to restore soil health and biodiversity.",
        },
        {
          question: 'HOW DO I KNOW MY BAG WAS MADE UNDER FAIR WORKING CONDITIONS?',
          answer:
            'Every behno bag is produced in partner factories that adhere to our rigorous "behno Standard," a guideline covering wages, health, safety, worker rights and beyond. We routinely audit and visit these facilities to ensure full compliance.',
        },
      ],
    };
  },
  computed: {
    parsedFaqs() {
      // Handle different prop types
      if (Array.isArray(this.faqs) && this.faqs.length > 0) {
        return this.faqs;
      }

      // If faqs is a string, try to parse it as JSON
      if (typeof this.faqs === 'string' && this.faqs.trim()) {
        try {
          const parsed = JSON.parse(this.faqs);
          return Array.isArray(parsed) ? parsed : this.defaultFaqs;
        } catch (e) {
          console.warn('Failed to parse FAQs JSON:', e);
          return this.defaultFaqs;
        }
      }

      // Fallback to default FAQs
      return this.defaultFaqs;
    },
    filteredFaqs() {
      if (!this.searchTerm.trim()) return this.parsedFaqs;
      const term = this.searchTerm.toLowerCase();
      return this.parsedFaqs.filter(
        (f) => f.question.toLowerCase().includes(term) || f.answer.toLowerCase().includes(term)
      );
    },
  },
  methods: {
    toggle(idx) {
      this.openIndex = this.openIndex === idx ? -1 : idx;
    },
    splitAnswer(answer) {
      // Split on double newlines to create proper paragraphs
      return answer.split('\n\n').filter((para) => para.trim());
    },
  },
};
</script>

<style scoped>
/*------------------------------------*/
/* Layout & Container                 */
/*------------------------------------*/

.faq-page {
  max-width: 820px;
  margin: 0 auto;
  padding: 40px 20px 80px;
}

.faq-header {
  text-align: center;
  margin-bottom: 40px;
}

.faq-title {
  font-size: 40px;
  line-height: 50px;
  font-family: 'Big Caslon', serif;
  font-weight: 500; /* Medium */
  margin: 0 0 12px;
  letter-spacing: 0;
  text-transform: none;
}

.faq-subtitle {
  font-size: 14px;
  line-height: 20px;
  font-family: 'Adobe Caslon Pro', serif;
  color: #000;
  margin: 0 0 12px;
}

/* Search Wrapper redesign */
.faq-search-wrapper {
  position: relative;
  width: 320px; /* 20rem */
  height: 40px; /* 2.5rem */
  margin: 0 auto 40px;
  box-shadow: 0 6px 24px -4px rgba(0, 0, 0, 0.1);
}

.faq-search-input {
  width: 100%;
  height: 100%;
  padding: 0 48px 0 16px; /* space for icon */
  border: 1px solid rgba(0, 0, 0, 0.05);
  border-radius: 0;
  font-size: 14px;
  font-family: 'Adobe Caslon Pro', serif;
  color: #000;
}

.faq-search-input::placeholder {
  color: #767674;
}

.search-icon {
  width: 24px;
  height: 24px;
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  font-size: 20px;
  color: #000;
}

/*------------------------------------*/
/* FAQ List                           */
/*------------------------------------*/

.faq-item {
  border-bottom: 1px solid #dadada;
  padding: 16px 0;
}

.faq-question {
  width: 100%;
  background: transparent;
  border: none;
  display: flex;
  gap: 16px;
  align-items: center;
  text-align: left;
  cursor: pointer;
  padding: 0;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  font-family: 'Helvetica Neue', sans-serif;
  color: #000;
}

/* Plus / Minus Icon */
.toggle-icon {
  width: 18px;
  height: 18px;
  position: relative;
  flex-shrink: 0;
}

.toggle-icon::before,
.toggle-icon::after {
  content: '';
  position: absolute;
  background-color: #000;
  transition: transform 0.3s ease;
}
.toggle-icon::before {
  top: 50%;
  left: 0;
  right: 0;
  height: 2px;
  transform: translateY(-50%);
}
.toggle-icon::after {
  left: 50%;
  top: 0;
  bottom: 0;
  width: 2px;
  transform: translateX(-50%);
}

.toggle-icon.open::after {
  transform: translateX(-50%) scaleY(0);
}

/* Answer */
.faq-answer {
  padding: 10px 24px 0 36px;
  font-size: 14px;
  font-family: 'Adobe Caslon Pro', serif;
  line-height: 20px;
  color: #000;
  overflow: hidden;
}

.faq-answer p {
  font-size: 14px;
  font-family: 'Adobe Caslon Pro', serif;
  line-height: 20px;
  color: #000;
  margin: 0 0 12px 0; /* Add margin between paragraphs */
}

.faq-answer p:last-child {
  margin-bottom: 0; /* Remove margin from last paragraph */
}

.faq-answer p:empty {
  min-height: 12px; /* Give empty paragraphs some height */
  margin: 0 0 12px 0; /* Keep consistent spacing */
}

/* Collapse Transition */
.faq-collapse-enter-active,
.faq-collapse-leave-active {
  transition: max-height 0.35s cubic-bezier(0.25, 0.1, 0.25, 1);
}
.faq-collapse-enter-from,
.faq-collapse-leave-to {
  max-height: 0;
}
.faq-collapse-enter-to,
.faq-collapse-leave-from {
  max-height: 600px;
}
</style>

<style>
/* Global styles for FAQ links - not scoped */
.faq-answer a.faq-link {
  font-family: 'Adobe Caslon Pro', serif !important;
  color: blue !important;
}
</style>
